import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { 
    BookOpen, ArrowRight, Calendar, Clock, 
    Search, TrendingUp, Zap, Users
} from 'lucide-react';
import SEO, { SEO_CONFIG } from '../components/SEO';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_design-scorecard/artifacts/dmpk6oxa_site%20grade%20pro%20logo%20a.png";

export default function BlogPage({ onRunAudit, onNavigate }) {
    const seo = SEO_CONFIG.blog;
    // Placeholder blog posts - content to be added later
    const blogPosts = [
        {
            id: 4,
            title: "What a Website Grade Really Means",
            excerpt: "Understanding your website score and how to interpret the metrics that matter for your business success.",
            category: "Education",
            icon: BookOpen,
            color: "green",
            date: "March 2026",
            readTime: "5 min read",
            slug: "blog-post-website-grade",
            published: true
        },
        {
            id: 1,
            title: "Why Your Website Isn't Converting (And How to Fix It)",
            excerpt: "Discover the most common conversion killers and learn actionable strategies to turn more visitors into customers.",
            category: "Conversion",
            icon: TrendingUp,
            color: "cyan",
            date: "Coming Soon",
            readTime: "8 min read",
            slug: null,
            published: false
        },
        {
            id: 2,
            title: "5 SEO Mistakes Small Businesses Make",
            excerpt: "Avoid these critical SEO errors that could be costing you valuable search traffic and potential customers.",
            category: "SEO",
            icon: Search,
            color: "magenta",
            date: "Coming Soon",
            readTime: "6 min read",
            slug: null,
            published: false
        },
        {
            id: 3,
            title: "How to Improve Website Speed Without Hiring a Developer",
            excerpt: "Simple, non-technical tips to boost your website's loading speed and improve user experience.",
            category: "Performance",
            icon: Zap,
            color: "yellow",
            date: "Coming Soon",
            readTime: "7 min read",
            slug: null,
            published: false
        }
    ];

    const colorClasses = {
        cyan: { bg: "from-cyan-500/20 to-blue-500/20", border: "border-cyan-500/20", text: "text-cyan-400", badge: "bg-cyan-500/20 text-cyan-400" },
        magenta: { bg: "from-pink-500/20 to-purple-500/20", border: "border-magenta/20", text: "text-magenta", badge: "bg-magenta/20 text-magenta" },
        yellow: { bg: "from-yellow-500/20 to-orange-500/20", border: "border-yellow-500/20", text: "text-yellow-400", badge: "bg-yellow-500/20 text-yellow-400" },
        green: { bg: "from-green-500/20 to-emerald-500/20", border: "border-green-500/20", text: "text-green-400", badge: "bg-green-500/20 text-green-400" }
    };

    return (
        <div className="min-h-screen bg-black text-white">
            <SEO title={seo.title} description={seo.description} keywords={seo.keywords} />
            
            <Navbar activePage="blog" onNavigate={onNavigate} onRunAudit={onRunAudit} />

            {/* Hero Section */}
            <section className="py-16 px-4">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-magenta/20 text-magenta text-sm font-medium rounded-full mb-6">
                        <BookOpen className="h-4 w-4" />
                        Website Optimization Insights
                    </div>
                    <h1 className="text-4xl md:text-5xl font-bold mb-4">
                        The SiteGradePro Blog
                    </h1>
                    <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                        Expert tips, guides, and insights to help you improve your website's 
                        performance, SEO, and conversions.
                    </p>
                </div>
            </section>

            {/* Blog Posts Grid */}
            <section className="py-8 px-4">
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {blogPosts.map((post) => {
                            const colors = colorClasses[post.color];
                            const Icon = post.icon;
                            
                            const handlePostClick = () => {
                                if (post.published && post.slug) {
                                    onNavigate(post.slug);
                                }
                            };
                            
                            return (
                                <Card 
                                    key={post.id} 
                                    onClick={handlePostClick}
                                    className={`bg-white/5 border-white/10 hover:${colors.border} transition-all group ${post.published ? 'cursor-pointer' : 'cursor-default opacity-80'}`}
                                    data-testid={`blog-card-${post.id}`}
                                >
                                    <CardContent className="p-0">
                                        {/* Post Header Image/Icon Area */}
                                        <div className={`h-48 bg-gradient-to-br ${colors.bg} flex items-center justify-center`}>
                                            <Icon className={`h-20 w-20 ${colors.text} opacity-50 group-hover:opacity-80 transition-opacity`} />
                                        </div>
                                        
                                        {/* Post Content */}
                                        <div className="p-6">
                                            <div className="flex items-center gap-3 mb-4">
                                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${colors.badge}`}>
                                                    {post.category}
                                                </span>
                                                <span className="text-gray-500 text-xs flex items-center gap-1">
                                                    <Calendar className="h-3 w-3" />
                                                    {post.date}
                                                </span>
                                            </div>
                                            
                                            <h2 className="text-xl font-bold text-white mb-3 group-hover:text-magenta transition-colors">
                                                {post.title}
                                            </h2>
                                            
                                            <p className="text-gray-400 mb-4 line-clamp-2">
                                                {post.excerpt}
                                            </p>
                                            
                                            <div className="flex items-center justify-between">
                                                <span className="text-gray-500 text-sm flex items-center gap-1">
                                                    <Clock className="h-4 w-4" />
                                                    {post.readTime}
                                                </span>
                                                {post.published ? (
                                                    <span className="text-magenta text-sm font-medium group-hover:underline flex items-center gap-1" data-testid={`read-post-${post.id}`}>
                                                        Read Article
                                                        <ArrowRight className="h-4 w-4" />
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-500 text-sm font-medium flex items-center gap-1">
                                                        Coming Soon
                                                        <ArrowRight className="h-4 w-4" />
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Newsletter Section */}
            <section className="py-20 px-4">
                <div className="max-w-3xl mx-auto">
                    <Card className="bg-gradient-to-br from-magenta/10 to-cyan-500/10 border-magenta/30">
                        <CardContent className="p-8 md:p-12 text-center">
                            <Users className="h-12 w-12 text-magenta mx-auto mb-6" />
                            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                                Stay Updated
                            </h2>
                            <p className="text-gray-400 mb-8">
                                Get the latest website optimization tips delivered to your inbox.
                                New articles coming soon!
                            </p>
                            <Button 
                                onClick={onRunAudit}
                                size="lg"
                                className="bg-white text-black font-bold uppercase tracking-widest hover:bg-gray-200 rounded-sm px-10 py-6 text-lg"
                            >
                                Get Your Free Audit First
                                <ArrowRight className="ml-2 h-5 w-5" />
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* Footer */}
            <Footer onNavigate={onNavigate} />
        </div>
    );
}
